import { createCanvas } from "@/libs/canvas"
import type { FramePixels } from "@/libs/cv/modules/types"
import { WebGLContextLostError } from "@/libs/gl/error"
import { fit, getImageSourceDimensions, isVideoFrame, type ImageSource } from "@/libs/image"

export type Options = {
  resizeWidth?: number
  resizeHeight?: number
}

let gl: WebGL2RenderingContext | null = null
let abortController = new AbortController()

const reset = () => {
  abortController.abort()
  abortController = new AbortController()
  gl = null
  source = undefined
  resized = undefined
}
const getGL = () => {
  if (gl) return gl

  const canvas = createCanvas(1, 1)
  gl = canvas.getContext("webgl2", { willReadFrequently: true }) as WebGL2RenderingContext

  if (gl.isContextLost())
    throw new WebGLContextLostError("Failed to readPixels: gl.CONTEXT_LOST_WEBGL")

  canvas.addEventListener(
    "webglcontextlost",
    (e) => {
      e.preventDefault()
      reset()
    },
    { signal: abortController.signal },
  )

  return gl
}
const retry = <R>(
  fn: () => R,
  nTimes: number,
  {
    onRetry,
    shouldThrowEarly,
  }: {
    onRetry?: (error: Error) => void
    shouldThrowEarly?: (error: Error) => boolean
  } = {},
): R => {
  try {
    return fn()
  } catch (e) {
    if (nTimes <= 0 || shouldThrowEarly?.(e as Error)) throw e
    onRetry?.(e as Error)
    return retry(fn, nTimes - 1, { onRetry, shouldThrowEarly })
  }
}

const TARGET_RESOLUTION = 480 // 480p

export const readPixels = (source: ImageSource): FramePixels => {
  let localGL: WebGL2RenderingContext
  const dims = getImageSourceDimensions(source)
  const { width, height } = fit({ width: dims.width, height: dims.height }, TARGET_RESOLUTION)
  const pixels = new Uint8Array(width * height * 4)

  retry(
    () =>
      getPixels(
        retry(() => (localGL = getGL()), 3),
        source,
        pixels,
        { resizeHeight: height, resizeWidth: width },
      ),
    1,
    {
      shouldThrowEarly: () => !localGL.isContextLost(),
      onRetry: () => {
        if (gl?.isContextLost()) reset()
      },
    },
  )

  return { width, height, format: "RGBA", data: pixels }
}

/** Cached GPU resources for source image */
let source: { framebuffer: WebGLFramebuffer; attachment: WebGLTexture } | undefined

/** Cached GPU resources for resized source image */
let resized: { framebuffer: WebGLFramebuffer; attachment: WebGLRenderbuffer } | undefined

/* General idea: https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/WebGL_best_practices#use_non-blocking_async_data_readback */
const getPixels = (
  gl: WebGL2RenderingContext,
  image: ImageSource,
  destination: ArrayBufferView,
  options?: Options,
) => {
  const { width: sourceWidth, height: sourceHeight } = getImageSourceDimensions(image)

  const width = options?.resizeWidth ?? sourceWidth
  const height = options?.resizeHeight ?? sourceHeight
  const format = "RGBA"

  // TODO: Handle `texture` and `framebuffer` recreation on `image` dimensions change
  // One possible scenario of this is a runtime change of the camera stream resolution, e.g. if LIQA were switching from low-res to hi-res video
  // But as of now there is no such use-case and is unlikely to appear
  if (!source) {
    const attachment = gl.createTexture()!
    gl.bindTexture(gl.TEXTURE_2D, attachment)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl[format], gl[format], gl.UNSIGNED_BYTE, image)
    gl.bindTexture(gl.TEXTURE_2D, null)

    const framebuffer = gl.createFramebuffer()!
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, attachment, 0)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)

    source = { framebuffer, attachment }
  }

  gl.bindTexture(gl.TEXTURE_2D, source.attachment)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl[format], gl[format], gl.UNSIGNED_BYTE, image)
  gl.bindTexture(gl.TEXTURE_2D, null)

  // TODO: Consider skipping the resize step if `width === sourceWidth && height === sourceHeight`
  // There is no such use-case now and is unlikely to appear
  /* Perform source resize */
  {
    // TODO: Handle `renderbuffer` and `framebuffer` recreation on `options.resizeWidth` and `options.resizeHeight` change
    // There is no such use-case now and is unlikely to appear
    if (!resized) {
      const attachment = gl.createRenderbuffer()!
      gl.bindRenderbuffer(gl.RENDERBUFFER, attachment)
      gl.renderbufferStorage(gl.RENDERBUFFER, gl[`${format}8`], width, height)
      gl.bindRenderbuffer(gl.RENDERBUFFER, null)

      const framebuffer = gl.createFramebuffer()!
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, attachment)
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)

      resized = { framebuffer, attachment }
    }

    const [sw, sh] = isVideoFrame(image)
      ? [image.visibleRect!.width, image.visibleRect!.height]
      : [sourceWidth, sourceHeight]

    // transfer `source` texture to `resized` renderbuffer thus performing resize
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, source.framebuffer)
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, resized.framebuffer)
    gl.blitFramebuffer(0, 0, sw, sh, 0, 0, width, height, gl.COLOR_BUFFER_BIT, gl.NEAREST)
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null)
  }

  // use the resized framebuffer as the source for readPixels if a resize operation has taken place
  if (resized) gl.bindFramebuffer(gl.READ_FRAMEBUFFER, resized.framebuffer)
  // this step is actually unreachable in the current implementation, but persists in the code for the algorithm clarity
  else gl.bindFramebuffer(gl.READ_FRAMEBUFFER, source.framebuffer)

  gl.finish()
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, destination)
}
